import { useEffect, useRef, useState } from "react";
import { api, resolveFileUrl } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";
import { Send, Timer, PlayCircle, X } from "lucide-react";

const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

/** Student quiz-taking UI: ready screen → server-started countdown → auto-submit at 0. */
export default function QuizTaker({ quiz, onSubmitted }) {
  const timed = !!quiz.time_limit_min;
  const [phase, setPhase] = useState(timed ? "ready" : "active"); // ready | active | expired
  const [remaining, setRemaining] = useState(null);
  const [answers, setAnswers] = useState({});
  const [dragging, setDragging] = useState(null);   // {qid, rightId} being dragged / selected
  const [busy, setBusy] = useState(false);
  const submittedRef = useRef(false);
  const answersRef = useRef(answers);
  answersRef.current = answers;

  const start = async () => {
    try {
      const { data } = await api.post(`/quizzes/${quiz.id}/start`);
      if (data.remaining_seconds !== null && data.remaining_seconds !== undefined) {
        if (data.remaining_seconds <= 0) { setPhase("expired"); return; }
        setRemaining(data.remaining_seconds);
      }
      setPhase("active");
    } catch (e) {
      toast.error(e.response?.data?.detail || "تعذر بدء الاختبار");
    }
  };

  useEffect(() => {
    if (!timed) { api.post(`/quizzes/${quiz.id}/start`).catch(() => {}); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Countdown + auto-submit
  useEffect(() => {
    if (phase !== "active" || remaining === null) return;
    if (remaining <= 0) { autoSubmit(); return; }
    const t = setTimeout(() => setRemaining(r => r - 1), 1000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, remaining]);

  const doSubmit = async (auto = false) => {
    if (submittedRef.current) return;
    submittedRef.current = true;
    setBusy(true);
    try {
      const { data } = await api.post(`/quizzes/${quiz.id}/submit`, { quiz_id: quiz.id, answers: answersRef.current });
      toast.success(auto ? `⏰ انتهى الوقت — تم التسليم تلقائياً. درجتك: ${data.score}%` : `✅ درجتك: ${data.score}%`);
      onSubmitted(data);
    } catch (e) {
      submittedRef.current = false;
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "خطأ في التسليم");
      if (e.response?.status === 403) setPhase("expired");
    } finally { setBusy(false); }
  };
  const autoSubmit = () => doSubmit(true);

  const setAns = (qid, val) => setAnswers(a => ({ ...a, [qid]: val }));

  const placeMatch = (qid, leftId, rightId) => {
    setAnswers(a => {
      const cur = { ...(a[qid] || {}) };
      // remove rightId from any other slot first
      Object.keys(cur).forEach(k => { if (cur[k] === rightId) delete cur[k]; });
      cur[leftId] = rightId;
      return { ...a, [qid]: cur };
    });
    setDragging(null);
  };

  if (phase === "ready") {
    return (
      <div className="text-center space-y-4 py-6" data-testid="quiz-ready-screen">
        <Timer className="w-12 h-12 mx-auto text-emerald-600" />
        <div className="text-lg font-semibold">هذا اختبار محدد بوقت</div>
        <div className="text-sm text-muted-foreground">
          المدة: <b>{quiz.time_limit_min} دقيقة</b> · عدد الأسئلة: <b>{(quiz.questions || []).length}</b>
          <br />يبدأ العدّ التنازلي فور الضغط على "ابدأ الآن" ويُسلَّم الاختبار تلقائياً عند انتهاء الوقت.
        </div>
        <Button onClick={start} size="lg" data-testid="quiz-start-btn"><PlayCircle className="w-5 h-5 me-2" />ابدأ الآن</Button>
      </div>
    );
  }

  if (phase === "expired") {
    return (
      <div className="text-center space-y-3 py-8" data-testid="quiz-expired">
        <Timer className="w-12 h-12 mx-auto text-red-500" />
        <div className="text-lg font-semibold text-red-600">انتهى وقت هذا الاختبار</div>
        <div className="text-sm text-muted-foreground">لا يمكن تسليم الإجابات بعد انتهاء المدة المحددة.</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {remaining !== null && (
        <div className={`sticky top-0 z-10 flex items-center justify-center gap-2 py-2 rounded-lg font-mono text-lg font-bold
          ${remaining <= 60 ? "bg-red-50 text-red-700 animate-pulse" : "bg-emerald-50 text-emerald-800"}`}
          data-testid="quiz-timer">
          <Timer className="w-5 h-5" />{fmt(Math.max(0, remaining))}
        </div>
      )}

      {(quiz.questions || []).map((q, i) => (
        <Card key={q.id} className="p-3 border">
          <div className="font-medium mb-2">{i + 1}. {q.text} <span className="text-xs text-muted-foreground">({q.points} درجة)</span></div>
          {q.image_url && <img src={resolveFileUrl(q.image_url)} alt="" className="max-h-64 rounded-lg border mb-3" />}

          {q.type === "mcq" && (
            <div className="space-y-1">
              {(q.options || []).map(o => (
                <label key={o.id} className="flex items-center gap-2 p-2 rounded hover:bg-muted/40 cursor-pointer">
                  <input type="radio" name={q.id} onChange={() => setAns(q.id, o.id)} checked={answers[q.id] === o.id} />
                  <span className="text-sm">{o.text}</span>
                </label>
              ))}
            </div>
          )}

          {q.type === "dropdown" && (
            <Select value={answers[q.id] || ""} onValueChange={v => setAns(q.id, v)}>
              <SelectTrigger className="w-full sm:w-72" data-testid={`dropdown-${q.id}`}><SelectValue placeholder="اختر الإجابة الصحيحة" /></SelectTrigger>
              <SelectContent>{(q.options || []).map(o => <SelectItem key={o.id} value={o.id}>{o.text}</SelectItem>)}</SelectContent>
            </Select>
          )}

          {q.type === "checkbox" && (
            <div className="space-y-1">
              {(q.options || []).map(o => {
                const arr = answers[q.id] || [];
                return (
                  <label key={o.id} className="flex items-center gap-2 p-2 rounded hover:bg-muted/40 cursor-pointer">
                    <input type="checkbox" checked={arr.includes(o.id)}
                      onChange={() => setAns(q.id, arr.includes(o.id) ? arr.filter(x => x !== o.id) : [...arr, o.id])} />
                    <span className="text-sm">{o.text}</span>
                  </label>
                );
              })}
              <p className="text-[11px] text-muted-foreground">يمكن اختيار أكثر من إجابة.</p>
            </div>
          )}

          {q.type === "tf" && (
            <div className="flex gap-3">
              <label className="flex items-center gap-2"><input type="radio" name={q.id} onChange={() => setAns(q.id, true)} checked={answers[q.id] === true} />صح / نعم</label>
              <label className="flex items-center gap-2"><input type="radio" name={q.id} onChange={() => setAns(q.id, false)} checked={answers[q.id] === false} />خطأ / لا</label>
            </div>
          )}

          {q.type === "short" && (
            <Input value={answers[q.id] || ""} onChange={e => setAns(q.id, e.target.value)} placeholder="اكتب إجابتك هنا" />
          )}

          {q.type === "matching" && (() => {
            const chosen = answers[q.id] || {};
            const usedRights = new Set(Object.values(chosen));
            const rightById = Object.fromEntries((q.right_items || []).map(r => [r.id, r.text]));
            return (
              <div className="space-y-3">
                {/* answers pool */}
                <div className="flex flex-wrap gap-2 p-2 rounded-lg bg-muted/40 min-h-[46px]" data-testid={`match-pool-${q.id}`}>
                  {(q.right_items || []).filter(r => !usedRights.has(r.id)).map(r => (
                    <span key={r.id} draggable
                      onDragStart={e => { e.dataTransfer.setData("text/plain", r.id); setDragging({ qid: q.id, rightId: r.id }); }}
                      onClick={() => setDragging(dragging?.rightId === r.id ? null : { qid: q.id, rightId: r.id })}
                      className={`px-3 py-1.5 rounded-full text-sm border cursor-grab select-none transition-colors
                        ${dragging?.rightId === r.id && dragging?.qid === q.id ? "bg-emerald-600 text-white border-emerald-600" : "bg-white hover:border-emerald-500"}`}>
                      {r.text}
                    </span>
                  ))}
                  {(q.right_items || []).every(r => usedRights.has(r.id)) && <span className="text-xs text-muted-foreground">تم توزيع كل الإجابات ✓</span>}
                </div>
                {/* left items with drop slots */}
                <div className="space-y-2">
                  {(q.left_items || []).map(l => (
                    <div key={l.id} className="flex items-center gap-2">
                      <div className="flex-1 p-2 rounded-lg border bg-white text-sm">{l.text}</div>
                      <span className="text-muted-foreground">←</span>
                      <div
                        onDragOver={e => e.preventDefault()}
                        onDrop={e => { e.preventDefault(); const rid = e.dataTransfer.getData("text/plain"); if (rid) placeMatch(q.id, l.id, rid); }}
                        onClick={() => { if (dragging?.qid === q.id) placeMatch(q.id, l.id, dragging.rightId); }}
                        data-testid={`match-slot-${l.id}`}
                        className={`flex-1 min-h-[40px] p-1.5 rounded-lg border-2 border-dashed flex items-center justify-between gap-1 text-sm transition-colors
                          ${chosen[l.id] ? "border-emerald-400 bg-emerald-50" : dragging?.qid === q.id ? "border-emerald-500 bg-emerald-50/50 cursor-pointer" : "border-border"}`}>
                        {chosen[l.id] ? (
                          <>
                            <span className="px-2">{rightById[chosen[l.id]]}</span>
                            <button onClick={e => { e.stopPropagation(); setAnswers(a => { const cur = { ...(a[q.id] || {}) }; delete cur[l.id]; return { ...a, [q.id]: cur }; }); }}
                              className="p-0.5 rounded-full hover:bg-white"><X className="w-3.5 h-3.5" /></button>
                          </>
                        ) : <span className="text-xs text-muted-foreground px-2">اسحب الإجابة هنا أو اضغطها ثم اضغط هنا</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

          {q.type === "image_label" && (
            <div className="space-y-2">
              {(q.labels || []).map(lb => (
                <div key={lb.num} className="flex items-center gap-2">
                  <span className="w-9 h-9 rounded-full bg-emerald-600 text-white flex items-center justify-center font-bold flex-shrink-0">{lb.num}</span>
                  <Input value={(answers[q.id] || {})[lb.num] || ""}
                    onChange={e => setAns(q.id, { ...(answers[q.id] || {}), [lb.num]: e.target.value })}
                    placeholder={`إجابة الرقم ${lb.num}`} data-testid={`imglabel-${q.id}-${lb.num}`} />
                </div>
              ))}
            </div>
          )}
        </Card>
      ))}
      <Button onClick={() => doSubmit(false)} disabled={busy} className="w-full" data-testid="submit-quiz">
        <Send className="w-4 h-4 me-2" />تسليم الإجابات
      </Button>
    </div>
  );
}
